/**
 * Screen health status logic.
 *
 * Pure functions for connection status (heartbeat age) and content-sync
 * status (playlist version comparison). Dashboard presentation adapters
 * live in `lib/dashboard/screenStatus.ts` and are used by Displays cards,
 * board filters, and Screens page counts.
 *
 * Content offline readiness (cacheable share of the live playlist) is a
 * separate axis — see `@ghostscreen/contracts` `deriveOfflineReadiness` and
 * `lib/offline-readiness/`.
 *
 * Data sources:
 * - `lastSeenAt` ← device/screen heartbeat (`lastHeartbeatAt`)
 * - `desiredPlaylistVersion` / `reportedPlaylistVersion` ← `/v1/device/sync`
 * - `syncFailure` / `activeSyncEvidence` are typed for future use only —
 *   no production source populates them yet; omit them so status stays
 *   `unknown` rather than inventing error/updating.
 */

/** Connection status derived from a device's last-seen/heartbeat timestamp. */
export type ScreenConnectionStatus = 'online' | 'offline' | 'unknown';

/** Content-synchronisation status derived from playlist version comparison. */
export type ScreenSyncStatus = 'error' | 'updating' | 'up-to-date' | 'outdated' | 'unknown';

/**
 * A genuine recorded synchronisation failure. Not currently populated by
 * any existing data source — see module-level notes.
 */
export interface RecordedSyncFailure {
  /** ISO-8601 timestamp of when the failure was recorded. */
  occurredAt: string;
  message?: string;
}

/**
 * Genuine evidence that synchronisation is actively in progress. Not
 * currently populated by any existing data source — see module-level notes.
 */
export interface ActiveSyncEvidence {
  /** ISO-8601 timestamp of when the active sync began. */
  startedAt: string;
}

export interface ConnectionStatusInput {
  /** ISO-8601 last-seen/heartbeat timestamp, or null/undefined if unknown. */
  lastSeenAt: string | null | undefined;
  /** Reference "now" for the comparison. Defaults to `new Date()`. Inject a fixed
   *  Date in tests for deterministic threshold-boundary behaviour. */
  now?: Date;
  /** Overrides the default online/offline threshold. Defaults to
   *  SCREEN_ONLINE_THRESHOLD_SECONDS. */
  thresholdSeconds?: number;
}

export interface SyncStatusInput {
  /** The playlist version the screen should be running, or null/undefined if unknown. */
  desiredPlaylistVersion: number | null | undefined;
  /** The playlist version the device last reported, or null/undefined if unknown. */
  reportedPlaylistVersion: number | null | undefined;
  /** A genuine recorded sync failure, if one exists. */
  syncFailure?: RecordedSyncFailure | null;
  /** Genuine evidence that sync is actively in progress, if any. */
  activeSyncEvidence?: ActiveSyncEvidence | null;
}

/**
 * Online/offline threshold, in seconds.
 *
 * Mirrors the SQL window in
 * packages/database/src/repositories/devices.ts (`ONLINE_WINDOW_SQL`,
 * `interval '120 seconds'`) — 2x the player's ~30–60s poll interval.
 * Values are deliberately mirrored, not shared across packages, so the
 * dashboard stays free of a database dependency for this constant.
 */
export const SCREEN_ONLINE_THRESHOLD_SECONDS = 120;

/** Parses a value into a valid Date, or null if missing/invalid. Never mutates its input. */
function parseValidTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** True for a finite, non-negative integer playlist version. */
function isValidPlaylistVersion(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value >= 0;
}

/**
 * Determines a screen's connection status from its last-seen/heartbeat
 * timestamp.
 *
 * - `online`: a valid timestamp within the threshold (inclusive — exactly
 *   at the threshold counts as online; a timestamp equal to `now` is also
 *   online).
 * - `offline`: a valid timestamp older than the threshold.
 * - `unknown`: the timestamp is missing, invalid, or later than `now`. A
 *   future heartbeat is not reliable evidence that the screen is online.
 */
export function getScreenConnectionStatus(input: ConnectionStatusInput): ScreenConnectionStatus {
  const lastSeen = parseValidTimestamp(input.lastSeenAt);
  if (!lastSeen) return 'unknown';

  const now = input.now ?? new Date();
  const thresholdSeconds = input.thresholdSeconds ?? SCREEN_ONLINE_THRESHOLD_SECONDS;

  const ageSeconds = (now.getTime() - lastSeen.getTime()) / 1000;

  if (ageSeconds < 0) return 'unknown';

  return ageSeconds <= thresholdSeconds ? 'online' : 'offline';
}

/**
 * Determines a screen's content-synchronisation status.
 *
 * Precedence (highest first):
 * 1. `error` — a genuine recorded sync failure was supplied.
 * 2. `updating` — genuine active-sync evidence was supplied (and no error).
 * 3. `up-to-date` / `outdated` — both desired and reported versions are
 *    valid; compared directly.
 * 4. `unknown` — insufficient reliable information (e.g. either version is
 *    missing or invalid).
 *
 * Missing values are never treated as a match. Differing versions alone
 * never produce `updating` — that requires explicit active-sync evidence.
 */
export function getScreenSyncStatus(input: SyncStatusInput): ScreenSyncStatus {
  if (input.syncFailure) {
    return 'error';
  }

  if (input.activeSyncEvidence) {
    return 'updating';
  }

  const desired = input.desiredPlaylistVersion;
  const reported = input.reportedPlaylistVersion;

  if (!isValidPlaylistVersion(desired) || !isValidPlaylistVersion(reported)) {
    return 'unknown';
  }

  return desired === reported ? 'up-to-date' : 'outdated';
}
